/* eslint-disable no-undef */
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid2 as Grid,
  ListItemButton,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import IntlMessages from "../../components/IntlMessages/IntlMessages";
import { UNInput, UNModal } from "un-react-ui-components";
import { useIntl } from "react-intl";
import cn from "classnames";
import { GRAY_60 } from "../../themes/colors";
import { useProofsActions } from "../../contexts/proofs/UseProofsActions";
import { useAuthContext } from "../../contexts/authentication/AuthenticationContext";
import { useProofsContext } from "../../contexts/proofs/ProofsContext";
import {
  DateFormat,
  getFormattedDate,
  getTotalRestrictions,
} from "../../util/utils";
import Proof from "../../core/models/Proof";
import { notify } from "../../libs/observables/notification";
import { StepCreateData } from "../../core/models/Step";
import { useStepsContext } from "../../contexts/steps/StepsContext";
import { useStepsActions } from "../../contexts/steps/UseStepsActions";
import {
  credentialOptions,
  CredentialOptionType,
  CredentialType,
  credentialTypes,
} from "../../defines/credential-options-types";

type StepModalProps = {
  open: boolean;
  requestId?: string;
  id?: string;
  onClose(): void;
};
const NewStepModal = ({ open, requestId, id, onClose }: StepModalProps) => {
  const intl = useIntl();
  const [title, setTitle] = useState<string>();
  const [errorTitle, setErrorTitle] = useState<string>("");

  const [selectedTypeCredenitial, setSelectedTypeCredenitial] =
    useState<string>(!id ? credentialTypes[0].value : "");
  const [selectedSubCredentialOption, setSelectedSubCredentialOption] =
    useState<string>(!id ? CredentialOptionType.Default : "");

  const [isMandatory, setIsMandatory] = useState<boolean>(false);
  const [isAllShared, setIsAllShared] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);

  const [selectedProof, setSelectedProof] = useState<Proof>();
  const { proofsResponseData } = useProofsContext();
  const { dispatchGetProofs } = useProofsActions();

  const { stepsResponseData, selectedStep, stepsError } = useStepsContext();
  const {
    dispatchCreateStep,
    dispatchUpdateSingleStep,
    dispatchGetStep,
    dispatchCleanStepsContext,
  } = useStepsActions();
  const { userInfo } = useAuthContext();
  //#region effects
  useEffect(() => {
    loadProofs();
  }, []);
  const loadProofs = () => {
    if (userInfo) {
      dispatchGetProofs({
        page: 1,
        size: 1000,
      }).catch();
    }
  };

  useEffect(() => {
    if (!id) {
      if (selectedStep && !stepsError) {
        notify("success")({ message: "Step created!" });
        onClose();
      }
    } else {
      if (loading && selectedStep && !stepsError) {
        notify("success")({ message: "Step updated!" });
        onClose();
      }
    }

    setLoading(false);
  }, [selectedStep, stepsError]);

  useEffect(() => {
    if (id) dispatchGetStep(id);
    return () => dispatchCleanStepsContext();
  }, []);

  useEffect(() => {
    if (selectedStep) {
      setTitle(selectedStep.title);
      setIsMandatory(selectedStep.is_mandatory);
      setIsAllShared(selectedStep.is_required_share_all);
      setSelectedTypeCredenitial(
        selectedStep.is_multiple
          ? credentialTypes[1].value
          : credentialTypes[0].value
      );
      if (selectedStep.is_multiple) {
        if (selectedStep.is_mandatory) {
          if (selectedStep.is_required_share_all) {
            setSelectedSubCredentialOption(
              CredentialOptionType.MultipleRequiredSharedAll
            );
          } else {
            setSelectedSubCredentialOption(
              CredentialOptionType.MultipleRequiredNoSharedAll
            );
          }
        } else {
          if (selectedStep.is_required_share_all) {
            setSelectedSubCredentialOption(
              CredentialOptionType.MultipleNotRequiredSharedAll
            );
          } else {
            setSelectedSubCredentialOption(
              CredentialOptionType.MultipleNotRequiredNoSharedAll
            );
          }
        }
      } else {
        if (selectedStep.is_mandatory) {
          setSelectedSubCredentialOption(CredentialOptionType.SingleRequired);
        } else {
          setSelectedSubCredentialOption(
            CredentialOptionType.SingleNotRequired
          );
        }
      }
      setSelectedProof(
        proofsResponseData?.data.filter(
          (item) => item.id === selectedStep.presentation_configuration_id
        )[0]
      );
    }
  }, [selectedStep]);
  //#endregion

  //#region Actions modal
  const onSave = () => {
    if (!title) {
      setErrorTitle("Request title is required!");
      return;
    }
    if (!selectedProof) {
      notify("error")({ message: "Select proof is required!" });
      return;
    }

    setLoading(true);
    let order = 0;
    if (id) {
      order = selectedStep?.order ?? 0;
    } else {
      order = stepsResponseData?.data?.length
        ? stepsResponseData.data[stepsResponseData.data.length - 1].order + 1
        : 0;
    }
    const step: StepCreateData = {
      id: id ?? id,
      presentation_configuration_id: selectedProof.id,
      presentation_configuration: selectedProof,
      integration_configuration: requestId,
      title: title,
      is_multiple: selectedTypeCredenitial === CredentialType.Multiple,
      is_mandatory: isMandatory,
      is_required_share_all: isAllShared,
      order: order,
    };
    if (!id) {
      dispatchCreateStep(step);
    } else {
      dispatchUpdateSingleStep(step);
    }
  };
  const onChangeSubCredentialOption = (value: string) => {
    switch (value) {
      case CredentialOptionType.SingleNotRequired:
        setIsMandatory(false);
        setIsAllShared(false);
        break;
      case CredentialOptionType.SingleRequired:
        setIsMandatory(true);
        setIsAllShared(false);
        break;
      case CredentialOptionType.MultipleNotRequiredNoSharedAll:
        setIsMandatory(false);
        setIsAllShared(false);
        break;
      case CredentialOptionType.MultipleNotRequiredSharedAll:
        setIsMandatory(false);
        setIsAllShared(true);
        break;
      case CredentialOptionType.MultipleRequiredNoSharedAll:
        setIsMandatory(true);
        setIsAllShared(false);
        break;
      case CredentialOptionType.MultipleRequiredSharedAll:
        setIsMandatory(true);
        setIsAllShared(true);
        break;
      case CredentialOptionType.Default:
        setIsMandatory(false);
        setIsAllShared(false);
        break;
    }
    setSelectedSubCredentialOption(value);
  };
  //#region end

  //#region Renderers
  const content = (
    <Box className="space-y-12">
      <Grid
        data-testid="newStepModal.filters"
        container
        alignItems="center"
        direction={"row"}
      >
        <Grid size={{ xs: 12 }} className="space-y-[1.875rem]">
          <UNInput
            id="step.title"
            data-testid={"step.title"}
            labelTranslationKey="step.new.title.label"
            value={title}
            onChange={setTitle}
            error={errorTitle}
          />
          <RadioGroup
            data-testid="newStepModal.credentialOptions"
            aria-label="Credential Options list"
            className="space-y-3"
            value={selectedTypeCredenitial}
          >
            {credentialTypes.map(({ value, labelId }, index) => (
              <React.Fragment key={value}>
                <ListItemButton
                  data-testid={`newStepModal.attr.${index}`}
                  className="p-0"
                  onClick={() => {
                    if (selectedTypeCredenitial !== value) {
                      onChangeSubCredentialOption(CredentialOptionType.Default);
                    }
                    setSelectedTypeCredenitial(value);
                  }}
                >
                  <Grid container className="w-full items-center space-x-3">
                    <Grid size={{ xs: "auto" }}>
                      <Radio
                        className="p-0"
                        value={value}
                        checked={selectedTypeCredenitial === value}
                      />
                    </Grid>
                    <Typography variant="body1" fontWeight={500}>
                      {intl.formatMessage({ id: labelId })}
                    </Typography>
                  </Grid>
                </ListItemButton>

                {/* Sub-radio group for selected type */}
                {selectedTypeCredenitial === value && (
                  <RadioGroup
                    className="ml-6 space-y-2 pt-2"
                    value={selectedSubCredentialOption}
                  >
                    {credentialOptions[value].map((opt) => (
                      <ListItemButton
                        key={opt.value}
                        className="p-0"
                        onClick={() => {
                          onChangeSubCredentialOption(opt.value);
                        }}
                      >
                        <Grid container className="items-start space-x-3">
                          <Grid size={{ xs: "auto" }}>
                            <Radio
                              className="p-0"
                              value={opt.value}
                              checked={
                                selectedSubCredentialOption === opt.value
                              }
                            />
                          </Grid>
                          <Box>
                            <Typography variant="body1" fontWeight={500}>
                              {intl.formatMessage({ id: opt.title })}
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              color={GRAY_60}
                            >
                              {intl.formatMessage({ id: opt.subTitle })}
                            </Typography>
                          </Box>
                        </Grid>
                      </ListItemButton>
                    ))}
                  </RadioGroup>
                )}
              </React.Fragment>
            ))}
          </RadioGroup>

          <Typography variant="body2" color={GRAY_60} sx={{ marginTop: "0px" }}>
            <IntlMessages id={"step.new.select.proof.label"} />
          </Typography>
          <RadioGroup
            data-testid="newStepModal.proofsList"
            aria-label="proofs list"
            className="space-y-3 bg-gray-background rounded-lg p-5"
            value={""}
            sx={{ marginTop: "0px" }}
          >
            {proofsResponseData?.data.map((item, index) => (
              <React.Fragment key={item.id}>
                <ListItemButton
                  data-testid={`newStepModal.proofsList.proof.${index}`}
                  className={cn(
                    "rounded-lg border border-solid border-gray-background px-3 py-4 bg-white"
                  )}
                  onClick={() => setSelectedProof(item)}
                >
                  {/* Schema */}
                  <Grid container className="w-full">
                    <Grid size="grow">
                      <Grid container>
                        <Box>
                          <Radio
                            className="p-0"
                            value={item.id}
                            checked={item.id === selectedProof?.id}
                          />
                        </Box>
                        <Box className="px-3">
                          <Typography variant="body1" fontWeight={500}>
                            {item.name}
                          </Typography>
                          <Box className="pt-3">
                            <Typography variant="body2">
                              {item?.requestedAttributes?.length}{" "}
                              {intl.formatMessage({
                                id: "step.new.modal.attributes",
                              })}{" "}
                              /{getTotalRestrictions(item?.requestedAttributes)}{" "}
                              {intl.formatMessage({
                                id: "step.new.modal.credentialDefinitions",
                              })}
                            </Typography>
                            <Typography variant="body2" color={GRAY_60}>
                              {getFormattedDate(
                                item.modifiedOn,
                                DateFormat.DayOfMonth
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </ListItemButton>
              </React.Fragment>
            ))}
          </RadioGroup>
        </Grid>
      </Grid>
    </Box>
  );

  const actions = (
    <>
      <Box />
      <Box className="flex space-x-[1.375rem]">
        <Button
          data-testid="newStepModal.cancel"
          variant="text"
          color="error"
          onClick={() => onClose()}
        >
          <IntlMessages id="cancel" />
        </Button>
        <Button data-testid="newStepModal.save" onClick={onSave}>
          <IntlMessages id="save" />
        </Button>
      </Box>
    </>
  );
  //#endregion

  return (
    <UNModal
      id="newStepModal"
      titleTranslationKey="step.new.title"
      showTitleBorder={false}
      open={open}
      loading={false}
      onClose={() => onClose()}
      actions={actions}
      maxWidth={"md"}
    >
      <Backdrop open={loading}>
        <CircularProgress data-testid="loader" />
      </Backdrop>
      {content}
    </UNModal>
  );
};

export { NewStepModal };
